document.addEventListener("DOMContentLoaded", function () {

  /* ============================================================
     CONFIG
     ============================================================ */

  // Example sketch image (the "SEVEN SEATER, L-SHAPE" reference).
  // Upload your sketch to Shopify (Settings > Files) and paste its URL here.
  const REFERENCE_IMG_URL = "https://cdn.shopify.com/s/files/1/0669/5600/1574/files/reference.png?v=1784890954";

  // OPTIONAL: force a specific chat launcher selector.
  // Leave "" to use the built-in detection (Shopify Inbox).
  // If you ever switch chat apps and it stops working, right-click your chat
  // bubble -> Inspect -> copy its CSS selector and paste it here.
  const CHAT_LAUNCHER_SELECTOR = "";

  // LAST-RESORT fallback. If the toggle can't be found (e.g. the widget uses a
  // closed shadow DOM), navigate to /?chat, which Shopify Inbox opens on load.
  // This reloads the page, so it's only used after every other attempt fails.
  const CHAT_URL_FALLBACK = true;

  // Intro message typed into the chat composer. {product} is replaced with the
  // selected product name.
  const CHAT_MESSAGE_TEMPLATE = "Hi,\nI want to submit a custom layout sketch for {product}";

  /* ============================================================
     Open the site's chat widget (SHOPIFY INBOX).

     Shopify Inbox renders its bubble inside a Web Component's shadow
     DOM. The clickable element is `.chat-toggle`, but the host tag /
     id varies by Inbox version (<shopify-chat>, #ShopifyChat,
     inbox-online-store-chat ...). So instead of guessing the host,
     we walk EVERY shadow root on the page and click the toggle.
     ============================================================ */

  // Collect every element on the page, descending into open shadow roots.
  function collectDeep(root, out) {
    out = out || [];
    let nodes;
    try { nodes = root.querySelectorAll("*"); } catch (e) { return out; }
    for (const el of nodes) {
      out.push(el);
      if (el.shadowRoot) collectDeep(el.shadowRoot, out);
    }
    return out;
  }

  function classString(el) {
    const c = el.className;
    if (typeof c === "string") return c;                 // normal element
    if (c && typeof c.baseVal === "string") return c.baseVal; // SVG element
    return "";
  }

  // Find the Shopify Inbox chat toggle anywhere in the (shadow) DOM.
  function findChatToggle() {
    const all = collectDeep(document);

    // 1) The known Shopify Inbox toggle class(es)
    for (const el of all) {
      if (/(^|\s)chat-toggle(\s|$)|chat.?activator|chat.?launcher|chat.?bubble/i.test(classString(el))) {
        return el;
      }
    }

    // 2) A button/role=button whose label points at the chat launcher
    for (const el of all) {
      const isButton = el.tagName === "BUTTON" || (el.getAttribute && el.getAttribute("role") === "button");
      if (!isButton) continue;
      const label = ((el.getAttribute && (el.getAttribute("aria-label") || el.getAttribute("title"))) || el.textContent || "").toLowerCase();
      if (/close|send|attach|emoji|minim|back|end chat/.test(label)) continue; // skip in-panel controls
      if (/chat|message|assistance|contact|help|support|need help|live chat/.test(label)) return el;
    }

    return null;
  }

  function fireClick(el) {
    try { el.click(); } catch (e) {}
  }

  function openSiteChat(attempt) {
    attempt = attempt || 0;

    // 0) Manual override
    if (CHAT_LAUNCHER_SELECTOR) {
      const forced = document.querySelector(CHAT_LAUNCHER_SELECTOR);
      if (forced) { fireClick(forced); return true; }
    }

    // 1) Shopify Inbox toggle (searched across all shadow roots)
    const toggle = findChatToggle();
    if (toggle) { fireClick(toggle); return true; }

    // 2) Provider APIs — only relevant if the chat app is ever changed
    const apiAttempts = [
      () => window.Intercom && window.Intercom("show"),
      () => window.$crisp && window.$crisp.push(["do", "chat:open"]),
      () => window.tidioChatApi && window.tidioChatApi.open(),
      () => window.Tawk_API && window.Tawk_API.maximize && window.Tawk_API.maximize(),
      () => window.zE && window.zE("messenger", "open"),
      () => window.GorgiasChat && window.GorgiasChat.open && window.GorgiasChat.open(),
    ];
    let apiHit = false;
    for (const fn of apiAttempts) {
      try { if (fn() !== undefined) apiHit = true; } catch (e) {}
    }
    if (apiHit) return true;

    // The Inbox bundle loads asynchronously — the toggle may not exist yet on
    // the first click. Retry for a few seconds before falling back.
    if (attempt < 16) {
      setTimeout(function () { openSiteChat(attempt + 1); }, 250);
      return false;
    }

    // 3) Last resort: let Shopify open the chat via URL (reloads the page)
    if (CHAT_URL_FALLBACK) {
      window.location.href = window.location.pathname + "?chat";
      return true;
    }

    console.warn("[custom-layout] Could not locate the chat toggle to open it.");
    return false;
  }

  /* ============================================================
     Pre-fill the Shopify Inbox chat composer with an intro message.
     The composer lives in the OPEN shadow root of #ShopifyChat /
     inbox-online-store-chat, inside .composer-bar-wrapper. It is a
     React-controlled field, so we set the value via the native setter
     and dispatch an input event so React registers the change.
     ============================================================ */
  function getChatHost() {
    return document.querySelector("#ShopifyChat") ||
           document.querySelector("inbox-online-store-chat");
  }

  function getChatComposer() {
    const host = getChatHost();
    const root = host && host.shadowRoot;
    if (!root) return null;
    return root.querySelector(
      '.composer-bar-wrapper textarea, .composer-bar-wrapper [contenteditable="true"], ' +
      'textarea, [contenteditable="true"], input[type="text"]'
    );
  }

  // React-safe value set for <textarea> / <input>.
  function setReactValue(el, value) {
    const proto = el.tagName === "TEXTAREA"
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const desc = Object.getOwnPropertyDescriptor(proto, "value");
    if (desc && desc.set) { desc.set.call(el, value); } else { el.value = value; }
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // For contenteditable composers.
  function fillEditable(el, value) {
    el.focus();
    try {
      document.execCommand("selectAll", false, null);
      document.execCommand("insertText", false, value);
    } catch (e) {}
    if (!el.textContent) {
      el.textContent = value;
      el.dispatchEvent(new InputEvent("input", { bubbles: true, inputType: "insertText", data: value }));
    }
  }

  // Wait for the composer to render (chat must be open first), then fill it.
  function fillChatMessage(message, attempt) {
    attempt = attempt || 0;
    const el = getChatComposer();
    if (el) {
      el.focus();
      if (el.tagName === "TEXTAREA" || el.tagName === "INPUT") {
        setReactValue(el, message);
      } else {
        fillEditable(el, message);
      }
      return true;
    }
    if (attempt < 24) { // ~6s total
      setTimeout(function () { fillChatMessage(message, attempt + 1); }, 250);
    }
    return false;
  }

  function buildChatMessage() {
    const name = (window.shopifyProduct && window.shopifyProduct.name) || "my selected product";
    return CHAT_MESSAGE_TEMPLATE.replace("{product}", name);
  }

  /* ============================================================
     1. MODAL — built once, appended to <body>, reused on demand
     ============================================================ */
  let modalOverlay = null;

  function buildModal() {
    if (modalOverlay) return modalOverlay;

    modalOverlay = document.createElement("div");
    modalOverlay.className = "lv-bp-modal-overlay";
    modalOverlay.setAttribute("role", "dialog");
    modalOverlay.setAttribute("aria-modal", "true");
    modalOverlay.setAttribute("aria-label", "Design Your Own Layout");

    modalOverlay.innerHTML = `
      <div class="lv-bp-modal">
        <button type="button" class="lv-bp-modal-close" aria-label="Close popup">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        <div class="lv-bp-modal-body">

          <h2 class="lv-bp-title">Design Your Own Layout</h2>
          <p class="lv-bp-subtitle" style="margin-bottom:10px";>This modular couch can be arranged in any configuration, including L-shaped, U-shaped, and many more. Simply sketch your desired layout on a piece of paper, then follow the steps below.</p>
          <!--<p class="lv-bp-subtitle" style="margin-bottom:10px";>Made to order in Canada, delivered on our standard timeline.</p>-->

          <!-- Process steps -->
          <div class="lv-bp-steps">
            <div class="lv-bp-step">
              <span class="lv-bp-step-num">1</span>
              <div class="lv-bp-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
              </div>
              <p class="lv-bp-step-label">Sketch Your Layout</p>
              <p class="lv-bp-step-sub">Draw your seats on paper in the arrangement you want. A simple top-down sketch is all it takes.</p>
            </div>
            <div class="lv-bp-step">
              <span class="lv-bp-step-num">2</span>
              <div class="lv-bp-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"></path><path d="m14.5 12.5 2-2"></path><path d="m11.5 9.5 2-2"></path><path d="m8.5 6.5 2-2"></path><path d="m17.5 15.5 2-2"></path></svg>
              </div>
              <p class="lv-bp-step-label">Add Dimensions</p>
              <p class="lv-bp-step-sub">Write down the total length and width available for your sofa. We will work out the rest.</p>
            </div>
            <div class="lv-bp-step">
              <span class="lv-bp-step-num">3</span>
              <div class="lv-bp-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
              </div>
              <p class="lv-bp-step-label">Send It to Us</p>
              <p class="lv-bp-step-sub">Take a photo and share it through the chat when you start the process. Our team takes it from there.</p>
            </div>
          </div>

          

          <!-- Intro copy -->
          
          <div class="lv-bp-desc">
            <p>Every space is different, and sometimes none of our ready-made arrangements are the right fit. With our custom layout service, you decide the number of seats and the shape, and we build it to fit your space. You don't need exact seat measurements. Tell us the total length and width you have available, and our team will design each section to suit the model you've chosen.</p>
          </div>

          <!-- What we need -->
          <h3 class="lv-bp-heading">What we need from you</h3>
          <p class="lv-bp-section-text">Just three things: how many seats you want, a sketch of how you'd like them arranged, and the total length and width of the space where the sofa will sit.</p>

          <!-- What happens next -->
          <h3 class="lv-bp-heading">What happens next</h3>
          <p class="lv-bp-section-text">Your sketch is only an estimate, and that's all we need. Once you share it, an Everyroom specialist will review your layout, refine the dimensions with you, and confirm a price. Before production begins, you'll receive a blueprint of your sofa to approve, so you know exactly what you're getting.</p>
          
          <!-- Timeline note -->
          <p class="lv-bp-note">Custom layouts are built and delivered on the same timeline as our regular sofas. No extra wait.</p>

          <!-- Small example sketch link -->
          <div class="lv-bp-example-wrap">
            <button type="button" class="lv-bp-example-link" id="lvBpExampleBtn" style="background:#ff9248";>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
              Click Here to View example sketch
            </button>
          </div>

          <!-- Primary CTA -->
          <p class="lv-bp-ready-note">Please have your sketch ready before you start.</p>
          <button type="button" class="lv-bp-cta" id="lvBpStartBtn">Start My Custom Layout</button>
          <p class="lv-bp-cta-hint">Opens our chat. A specialist replies during business hours.</p>

          <!-- Product context (moved to bottom) -->
          <div class="lv-bp-context-row">
            <span class="lv-bp-product-context" id="lvBpProductContext" style="display:none;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 8v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8"></path><path d="M2 8h20v0a3 3 0 0 0-3-3H5a3 3 0 0 0-3 3v0z"></path></svg>
              Designing for:&nbsp;<strong id="lvBpProductName"></strong>
            </span>
          </div>

        </div>
      </div>
    `;

    // ---- Close behaviour: X button, backdrop click, Escape key ----
    modalOverlay.querySelector(".lv-bp-modal-close").addEventListener("click", closeModal);
    modalOverlay.addEventListener("click", function (e) {
      if (e.target === modalOverlay) closeModal();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && modalOverlay.classList.contains("is-open")) closeModal();
    });

    // ---- Example sketch button: open reference image in a new tab ----
    modalOverlay.querySelector("#lvBpExampleBtn").addEventListener("click", function () {
      window.open(REFERENCE_IMG_URL, "_blank", "noopener");
    });

    // ---- Primary CTA: close the modal, then open the site chat ----
    modalOverlay.querySelector("#lvBpStartBtn").addEventListener("click", function () {
      const message = buildChatMessage();
      closeModal();
      // small delay so the modal is gone before the chat opens
      setTimeout(function () {
        openSiteChat();
        fillChatMessage(message); // waits for the composer to render, then types
      }, 150);
    });

    document.body.appendChild(modalOverlay);
    return modalOverlay;
  }

  function openModal() {
    const overlay = buildModal();

    // Show which product the user opened the popup from
    const name = (window.shopifyProduct && window.shopifyProduct.name) || "";
    const ctx = overlay.querySelector("#lvBpProductContext");
    if (name) {
      overlay.querySelector("#lvBpProductName").textContent = name;
      ctx.style.display = "inline-flex";
    } else {
      ctx.style.display = "none";
    }

    overlay.classList.add("is-open");
    document.documentElement.classList.add("lv-bp-modal-lock"); // lock page scroll
  }

  function closeModal() {
    if (!modalOverlay) return;
    modalOverlay.classList.remove("is-open");
    document.documentElement.classList.remove("lv-bp-modal-lock");
  }

  /* ============================================================
     2. SQUARE "MORE CONFIGURATIONS" TILE in the Arrangements group
     ============================================================ */
  const injectCustomTiles = function () {
    const optionGroups = document.querySelectorAll('.lv-option-box[data-tpl="Arrangements"] .lv-option-group');

    optionGroups.forEach(group => {
      if (group.querySelector('.custom-upload-wrapper')) return;

      const customTile = document.createElement('div');
      customTile.className = 'lv-option-wrapper custom-upload-wrapper';
      customTile.style.position = 'relative';
      customTile.innerHTML = `
        <a href="javascript:void(0);" class="lv-option-item lv-option-item-outer lv-option-item-link lv-option-hover-effect__zoom lv-custom-tile-link" aria-label="More configurations" style="display: block; text-decoration: none;">
          <div class="lv-custom-tile">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lv-custom-tile-icon" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1"></rect>
              <rect x="14" y="3" width="7" height="7" rx="1"></rect>
              <rect x="3" y="14" width="7" height="7" rx="1"></rect>
              <line x1="17.5" y1="14" x2="17.5" y2="21"></line>
              <line x1="14" y1="17.5" x2="21" y2="17.5"></line>
            </svg>
            <span class="lv-custom-tile-text">More<br>configurations</span>
          </div>
        </a>
      `;

      customTile.querySelector('a').addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });

      group.appendChild(customTile);
    });
  };

  const observer = new MutationObserver(injectCustomTiles);
  observer.observe(document.body, { childList: true, subtree: true });

  // Run once immediately in case the option group is already rendered
  injectCustomTiles();
});